/**
 * Sustained spell visuals (DESIGN 6.4): beams (thick throbbing body, running
 * pulses, flaring hit point, a bounce off a crossed beam), sprays as their
 * true cone with a stream of puffs and particles, arcs as crackling strips
 * (the bodies, puffs, flares and strips from the effects library, spellArt.ts),
 * and hazards: barrier / wall segments with a 2.5D front face and an hp
 * hint, mines pulsing, storms as animated particle bands, auras as dashed
 * rings. Plus the aim ghost and cursor reticle that show what the current
 * cast mode would do with the current queue (pillar 4 "every spell has a
 * preview").
 *
 * Beams and sprays leave the staff tip, STAFF_TIP_H above the feet; arcs
 * fly at half body height. Hazard chord lists are cached per hazard id.
 */

import { arcSegments, BARRIER, lineSegments, MINES, PRIORITY, ringSegments, SPRAY, type Element, type Segment, type Vec2 } from '@shared/index.js';
import type { ViewArc, ViewBeam, ViewHazard, ViewSpray } from '../state/viewModel.js';
import { elementColor, ELEMENT_SWATCH, mixHex, rgba, UI } from '../palette.js';
import { circlePath, clamp01, drawGlow, hashRand, hexPattern, STAFF_TIP_H, TAU, UNIT, WALL_H, type Ctx } from './gfx.js';
import { drawElementGlyph } from './glyphs.js';
import type { Camera } from './camera.js';
import type { Particles } from './particles.js';
import { drawField } from './depthFx.js';
import { art } from './sprites.js';
import { drawMineArt, drawPoolArt, drawWallFace, emitSprayPuffs, fxAt, fxElement, fxHas, fxStrip } from './spellArt.js';

const ARC_H = 0.5;
const BEAM_SEGMENTS = 10;

function primaryOf(el: readonly Element[]): Element {
  let best: Element = el[0] ?? 'arcane';
  for (const e of el) if (PRIORITY[e] > PRIORITY[best]) best = e;
  return best;
}

// ------------------------------------------------------------------ beams

/** Where a caster's staff tip is on screen: the sprite's anchor when the body was drawn from a set, else STAFF_TIP_H above the feet. */
function tipOf(cam: Camera, owner: number, x: number, y: number): { x: number; y: number } {
  const tip = art.set('wizard')?.point('staff_tip', owner) ?? art.set('shaman')?.point('staff_tip', owner);
  return tip ? { x: cam.sx(x) + tip.x, y: cam.sy(y) + tip.y } : { x: cam.sx(x), y: cam.sy(y) - STAFF_TIP_H * UNIT };
}

/**
 * A beam: a thick glowing body that throbs, a white-hot core, bright pulses running from the staff to where it
 * lands, its other elements riding along as coloured dashes, and a flaring hit point. Crossed with another beam it
 * bounces (beamCross.ts): the first leg ends at the crossing, which burns like a star, and the bounce leaves from
 * there, thicker by its multiplier, every element of both beams' statuses riding it.
 */
export function drawBeam(ctx: Ctx, cam: Camera, b: ViewBeam, time: number): void {
  const u = UNIT;
  const lift = STAFF_TIP_H * u;
  const { x: x0, y: y0 } = tipOf(cam, b.o, b.rx, b.ry);
  const prim = primaryOf(b.el);
  const col = elementColor(prim);
  const bo = b.bo;
  const x1 = bo ? cam.sx(bo.x) : cam.sx(b.rx + Math.cos(b.facing) * b.len);
  const y1 = (bo ? cam.sy(bo.y) : cam.sy(b.ry + Math.sin(b.facing) * b.len)) - lift;
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  beamLeg(ctx, x0, y0, x1, y1, b, prim, 1, 0, time);
  drawGlow(ctx, col, x0, y0, 52 + Math.sin(time * 9) * 8);
  if (bo) {
    const x2 = cam.sx(bo.x + Math.cos(bo.ang) * bo.len), y2 = cam.sy(bo.y + Math.sin(bo.ang) * bo.len) - lift;
    beamLeg(ctx, x1, y1, x2, y2, b, prim, bo.mul, 1.3, time);
    // the crossing: a white star that throbs and spins
    const s = 1 + 0.25 * Math.sin(time * 30);
    drawGlow(ctx, '#FFFFFF', x1, y1, 70 * s);
    drawGlow(ctx, col, x1, y1, 130 * s);
    ctx.strokeStyle = rgba('#FFFFFF', 0.9);
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < 4; i++) {
      const a = time * 3 + (i * Math.PI) / 4;
      const r = (i % 2 === 0 ? 22 : 14) * s;
      ctx.moveTo(x1 - Math.cos(a) * r, y1 - Math.sin(a) * r);
      ctx.lineTo(x1 + Math.cos(a) * r, y1 + Math.sin(a) * r);
    }
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
    fxAt(ctx, 'fx-beam', `tip-${fxElement(prim)}`, time * 1.6, x1, y1, 1, false, 1.5 * s);
    ctx.globalCompositeOperation = 'lighter';
    hitPoint(ctx, x2, y2, prim, bo.hit >= 0, bo.mul, time);
  } else {
    hitPoint(ctx, x1, y1, prim, b.hit >= 0, 1, time);
  }
  ctx.globalCompositeOperation = 'source-over';
}

/** One straight run of a beam from (x0, y0) to (x1, y1); `power` thickens it (a bounce's multiplier). */
function beamLeg(ctx: Ctx, x0: number, y0: number, x1: number, y1: number, b: ViewBeam, prim: Element, power: number, phase: number, time: number): void {
  const u = UNIT;
  const col = elementColor(prim);
  const dx = x1 - x0, dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  const wob = 0.08 * u;
  const throb = 1 + 0.18 * Math.sin(time * 16 + phase);
  const thick = Math.sqrt(power);
  const polyline = (ph: number, amp: number): void => {
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    for (let i = 1; i <= BEAM_SEGMENTS; i++) {
      const t = i / BEAM_SEGMENTS;
      const fade = i === BEAM_SEGMENTS ? 0 : 1;
      const off = Math.sin(time * 23 + i * 1.7 + ph + phase) * amp * fade;
      ctx.lineTo(x0 + dx * t + nx * off, y0 + dy * t + ny * off);
    }
  };
  ctx.strokeStyle = rgba(col, 0.22);
  ctx.lineWidth = 40 * thick * throb;
  polyline(0, wob);
  ctx.stroke();
  ctx.strokeStyle = rgba(col, 0.55);
  ctx.lineWidth = 20 * thick * throb;
  polyline(0, wob);
  ctx.stroke();
  // the body: the effects library's strip in the lead element, flowing toward where it lands; strokes until it loads
  ctx.globalCompositeOperation = 'source-over';
  const strip = fxStrip(ctx, 'fx-beam', `beam-${fxElement(prim)}`, time + phase, x0, y0, x1, y1, 1.3 * thick * (1 + 0.08 * Math.sin(time * 16 + phase)), b.id * 11);
  ctx.globalCompositeOperation = 'lighter';
  if (!strip) {
    ctx.strokeStyle = power > 1 ? mixHex(col, '#FFFFFF', 0.5 + 0.5 * Math.sin(time * 14)) : col;
    ctx.lineWidth = 9 * thick;
    polyline(0, wob);
    ctx.stroke();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 3 * thick;
    polyline(0, wob);
    ctx.stroke();
  }
  // pulses of power running down the beam
  ctx.strokeStyle = rgba('#FFFFFF', strip ? 0.45 : 0.75);
  ctx.lineWidth = 7 * thick;
  ctx.setLineDash([10, 46]);
  ctx.lineDashOffset = -time * 520;
  polyline(0, wob);
  ctx.stroke();
  // secondary elements ride the beam as moving dashes in their own colours
  let k = 0;
  for (const e of b.el) {
    if (e === prim) continue;
    ctx.strokeStyle = elementColor(e);
    ctx.lineWidth = 4 * thick;
    ctx.setLineDash([8, 18]);
    ctx.lineDashOffset = -time * 400 - k * 8;
    polyline(Math.PI / 2 + k, wob * 1.8);
    ctx.stroke();
    k++;
  }
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;
}

/** Where a beam lands: a flare and a ring when it is on a body, a smaller splash on anything else. */
function hitPoint(ctx: Ctx, x: number, y: number, prim: Element, onBody: boolean, power: number, time: number): void {
  const col = elementColor(prim);
  const s = 1 + 0.2 * Math.sin(time * 24);
  // the effects library's flare, turning, bigger on a body and on a bounce
  ctx.globalCompositeOperation = 'source-over';
  fxAt(ctx, 'fx-beam', `tip-${fxElement(prim)}`, time, x, y, onBody ? 1 : 0.8, false, (onBody ? 1.25 : 0.9) * Math.sqrt(power));
  ctx.globalCompositeOperation = 'lighter';
  if (!onBody) {
    drawGlow(ctx, col, x, y, 48 * s);
    return;
  }
  drawGlow(ctx, col, x, y, 120 * s * Math.sqrt(power));
  drawGlow(ctx, '#FFFFFF', x, y, 34 * s);
  ctx.strokeStyle = rgba('#FFFFFF', 0.85);
  ctx.lineWidth = 2.5;
  circlePath(ctx, x, y, (10 + 4 * ((time * 3) % 1)) * Math.sqrt(power));
  ctx.stroke();
}

/** Impact sparks where a beam lands, thrown back off the target; a shower off a crossing too. */
export function emitBeamSparks(particles: Particles, b: ViewBeam, dt: number): void {
  const bo = b.bo;
  const ang = bo ? bo.ang : b.facing;
  const ox = bo ? bo.x : b.rx, oy = bo ? bo.y : b.ry;
  const len = bo ? bo.len : b.len;
  const ex = ox + Math.cos(ang) * len, ey = oy + Math.sin(ang) * len;
  const onBody = (bo ? bo.hit : b.hit) >= 0;
  const n = Math.min(10, Math.round(dt * (onBody ? 420 : 120)));
  for (const e of b.el) particles.burst(e, ex, ey, STAFF_TIP_H, n, onBody ? 5 : 2.5);
  if (bo) for (const e of b.el) particles.burst(e, bo.x, bo.y, STAFF_TIP_H, Math.min(6, Math.round(dt * 200)), 4);
}

// ------------------------------------------------------------------ sprays

/**
 * A spray is the cone the server hits with (sprayCone.ts): apex at the caster's feet, `range` and `half` from the
 * snapshot. On the ground it is shaded bright through its full-strength core and fading over the falloff band, with
 * a crisp edge where it stops; the stream's particles leave the staff and land inside it, out to that edge.
 */
export function drawSpray(ctx: Ctx, cam: Camera, s: ViewSpray, particles: Particles, dt: number, time: number): void {
  const u = UNIT;
  const ang = s.facing;
  const ax = cam.sx(s.rx), ay = cam.sy(s.ry);
  const reach = s.range * u;
  const prim = primaryOf(s.el);
  const col = elementColor(prim);
  ctx.globalCompositeOperation = 'lighter';
  const flicker = 0.85 + 0.15 * Math.sin(time * 23);
  const g = ctx.createRadialGradient(ax, ay, 0, ax, ay, reach);
  g.addColorStop(0, rgba(col, 0.05));
  g.addColorStop(0.25, rgba(col, 0.2 * flicker));
  g.addColorStop(SPRAY.falloffFrom, rgba(col, 0.2 * flicker));
  g.addColorStop(1, rgba(col, 0.2 * SPRAY.edgeScale * flicker));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.arc(ax, ay, reach, ang - s.half, ang + s.half);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(col, 0.25);
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.strokeStyle = rgba(col, 0.7);
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.arc(ax, ay, reach, ang - s.half, ang + s.half);
  ctx.stroke();
  const { x: tx, y: ty } = tipOf(cam, s.o, s.rx, s.ry);
  drawGlow(ctx, col, tx, ty, 40 + Math.sin(time * 11) * 6);
  ctx.globalCompositeOperation = 'source-over';
  // the stream: the effects library's puffs (flame tongues, drops, frost, steam...) flying out to the cone's edge
  // over ~18 particles per frame at 60 fps
  emitSprayPuffs(s.rx, s.ry, STAFF_TIP_H, ang, s.half, s.range, s.el, dt);
  const total = Math.min(36, Math.round(dt * 1100));
  const per = Math.max(1, Math.round(total / s.el.length));
  for (const e of s.el) particles.sprayCone(e, s.rx, s.ry, STAFF_TIP_H, ang, s.half, s.range, per);
}

// ------------------------------------------------------------------ arcs

export function drawArc(ctx: Ctx, cam: Camera, a: ViewArc, time: number): void {
  if (a.pts.length < 2) return;
  const u = UNIT;
  const frame = Math.floor(time * 20); // re-roll jitter every 50 ms
  const flicker = 0.7 + 0.3 * hashRand(a.id, frame);
  const col = ELEMENT_SWATCH.lightning.core;
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalAlpha = flicker;
  const path = (): void => {
    ctx.beginPath();
    for (let s = 0; s < a.pts.length - 1; s++) {
      const p = a.pts[s]!, q = a.pts[s + 1]!;
      const start = s === 0 ? tipOf(cam, a.o, p[0], p[1]) : null;
      const x0 = start ? start.x : cam.sx(p[0]), y0 = start ? start.y : cam.sy(p[1]) - ARC_H * u;
      const x1 = cam.sx(q[0]), y1 = cam.sy(q[1]) - ARC_H * u;
      const dx = x1 - x0, dy = y1 - y0;
      const l = Math.hypot(dx, dy) || 1;
      const nx = -dy / l, ny = dx / l;
      if (s === 0) ctx.moveTo(x0, y0);
      for (let i = 1; i < 8; i++) {
        const t = i / 8;
        const j = (hashRand(a.id * 31 + s, frame * 8 + i) - 0.5) * 0.5 * u;
        ctx.lineTo(x0 + dx * t + nx * j, y0 + dy * t + ny * j);
      }
      ctx.lineTo(x1, y1);
    }
  };
  ctx.strokeStyle = rgba(col, 0.3);
  ctx.lineWidth = 10;
  path();
  ctx.stroke();
  // each jump as the effects library's crackling arc strip; thin strokes until it loads
  ctx.globalCompositeOperation = 'source-over';
  let strips = true;
  for (let s = 0; s < a.pts.length - 1 && strips; s++) {
    const p = a.pts[s]!, q = a.pts[s + 1]!;
    const start = s === 0 ? tipOf(cam, a.o, p[0], p[1]) : null;
    const x0 = start ? start.x : cam.sx(p[0]), y0 = start ? start.y : cam.sy(p[1]) - ARC_H * u;
    strips = fxStrip(ctx, 'fx-arc', 'arc', time + s * 0.07 + a.id * 0.13, x0, y0, cam.sx(q[0]), cam.sy(q[1]) - ARC_H * u, 1.2, s * 9 + a.id * 5, flicker);
  }
  ctx.globalCompositeOperation = 'lighter';
  if (!strips) {
    ctx.strokeStyle = col;
    ctx.lineWidth = 3;
    path();
    ctx.stroke();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1;
    path();
    ctx.stroke();
  }
  // joint flashes at every struck point
  for (let s = 1; s < a.pts.length; s++) {
    const q = a.pts[s]!;
    drawGlow(ctx, col, cam.sx(q[0]), cam.sy(q[1]) - ARC_H * u, 40);
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
}

// ------------------------------------------------------------------ hazards

interface SegCache { segs: Segment[]; seen: number }
const segCache = new Map<number, SegCache>();

/** Chord list for an arc / ring / line hazard, cached by id (hazards never move). */
export function hazardSegments(h: Pick<ViewHazard, 'id' | 'rx' | 'ry' | 'geo' | 'ang' | 'r'>, time: number): Segment[] {
  const hit = segCache.get(h.id);
  if (hit) { hit.seen = time; return hit.segs; }
  const c: Vec2 = { x: h.rx, y: h.ry };
  let segs: Segment[];
  switch (h.geo) {
    case 'arc': segs = arcSegments(c, h.ang, BARRIER.arcDeg, h.r > 0 ? h.r : BARRIER.arcRadius, 10); break;
    case 'ring': segs = ringSegments(c, h.r > 0 ? h.r : BARRIER.ringRadius, 24); break;
    case 'line': segs = lineSegments(c, h.ang, BARRIER.lineLength, 2); break;
    case 'point': segs = []; break;
  }
  segCache.set(h.id, { segs, seen: time });
  return segs;
}

/** Drop cached chords for hazards not seen for 2 s. */
export function sweepHazardCache(time: number): void {
  segCache.forEach((v, k) => { if (time - v.seen > 2) segCache.delete(k); });
}

function wallSolid(h: ViewHazard): 'earth' | 'ice' {
  return h.el.includes('ice') ? 'ice' : 'earth';
}

/** Floor-level hazards (DESIGN 6.2 layer 2): mines, storms, auras, and the footprint under walls. */
export function drawHazardFloor(ctx: Ctx, cam: Camera, h: ViewHazard, time: number, particles: Particles, dt: number): void {
  const u = UNIT;
  const px = cam.sx(h.rx), py = cam.sy(h.ry);
  switch (h.k) {
    case 'mine': {
      const payload = h.el.find((e) => e !== 'shield') ?? 'arcane';
      const col = elementColor(payload);
      const pulse = 1 + 0.15 * Math.sin(time * TAU + h.id);
      // trigger radius ring: every mine hurts everyone, so everyone gets to see it
      ctx.strokeStyle = rgba('#FFFFFF', 0.35);
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.ellipse(px, py, (h.r > 0 ? h.r : MINES.triggerRadius) * u, (h.r > 0 ? h.r : MINES.triggerRadius) * u * 0.45, 0, 0, TAU);
      ctx.stroke();
      ctx.setLineDash([]);
      // the rune plate from the effects library, glowing in its element; the vector puck below until it lands
      if (drawMineArt(ctx, cam, h, payload, time)) {
        ctx.globalCompositeOperation = 'lighter';
        drawGlow(ctx, col, px, py - 1, 30 * pulse);
        ctx.globalCompositeOperation = 'source-over';
        return;
      }
      ctx.fillStyle = rgba(col, 0.85);
      ctx.beginPath();
      ctx.ellipse(px, py, 0.26 * u * pulse, 0.14 * u * pulse, 0, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = ELEMENT_SWATCH[payload].dark;
      drawElementGlyph(ctx, payload, px, py - 1, 0.18 * u);
      ctx.globalCompositeOperation = 'lighter';
      drawGlow(ctx, col, px, py, 28 * pulse);
      ctx.globalCompositeOperation = 'source-over';
      return;
    }
    case 'field':
      drawField(ctx, cam, h, time);
      return;
    case 'pool': {
      // A Lava boulder's burning pool: molten core, crusted rim, rising embers. Fades in its last second.
      const r = (h.r > 0 ? h.r : 1.6) * u;
      const fade = Math.min(1, h.t);
      if (drawPoolArt(ctx, cam, h, time)) {
        if (Math.random() < dt * 10 * fade) particles.burst('fire', h.rx + (Math.random() - 0.5) * h.r, h.ry + (Math.random() - 0.5) * h.r * 0.5, 0.1, 1, 1.5);
        return;
      }
      const heave = 1 + 0.04 * Math.sin(time * 3 + h.id);
      ctx.fillStyle = rgba(ELEMENT_SWATCH.lava.dark, 0.9 * fade);
      ctx.beginPath();
      ctx.ellipse(px, py, r * 1.08, r * 0.5, 0, 0, TAU);
      ctx.fill();
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = rgba(ELEMENT_SWATCH.lava.core, 0.75 * fade);
      ctx.beginPath();
      ctx.ellipse(px, py, r * heave, r * 0.45 * heave, 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = rgba(ELEMENT_SWATCH.lava.soft, 0.55 * fade);
      ctx.beginPath();
      ctx.ellipse(px + Math.sin(time * 1.7 + h.id) * r * 0.2, py, r * 0.45, r * 0.2, 0, 0, TAU);
      ctx.fill();
      drawGlow(ctx, ELEMENT_SWATCH.lava.core, px, py, r * 1.4 * fade);
      ctx.globalCompositeOperation = 'source-over';
      if (Math.random() < dt * 14 * fade) particles.burst('fire', h.rx + (Math.random() - 0.5) * h.r, h.ry + (Math.random() - 0.5) * h.r * 0.5, 0.1, 1, 1.5);
      return;
    }
    case 'aura': {
      const el = h.el.filter((e) => e !== 'shield');
      const col = el.length > 0 ? elementColor(el[0]!) : UI.gold;
      const r = (h.r > 0 ? h.r : 3) * u;
      ctx.fillStyle = rgba(col, 0.07);
      ctx.beginPath();
      ctx.ellipse(px, py, r, r * 0.45, 0, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = rgba(col, 0.6);
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 8]);
      ctx.lineDashOffset = -time * 20;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.lineDashOffset = 0;
      let i = 0;
      for (const e of el) {
        ctx.fillStyle = elementColor(e);
        drawElementGlyph(ctx, e, px + (i - (el.length - 1) / 2) * 14, py - r * 0.45 - 10, 11);
        i++;
      }
      return;
    }
    case 'storm': {
      const segs = hazardSegments(h, time);
      const els = h.el.filter((e) => e !== 'shield');
      const prim = els[0] ?? 'lightning';
      ctx.strokeStyle = rgba(elementColor(prim), 0.25);
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      for (const [a, b] of segs) {
        ctx.moveTo(cam.sx(a.x), cam.sy(a.y));
        ctx.lineTo(cam.sx(b.x), cam.sy(b.y));
      }
      ctx.stroke();
      ctx.setLineDash([]);
      // with the columns drawn from the effects library (in the body pass) the particles only add a little life
      const columns = fxHas('fx-storm', `storm-${fxElement(prim)}`);
      const perSeg = Math.max(1, Math.round(dt * 60 * 0.5));
      for (const e of els) for (const [a, b] of segs) if (!columns || Math.random() < 0.3) particles.stormBand(e, a.x, a.y, b.x, b.y, 1.0, perSeg);
      return;
    }
    case 'barrier':
    case 'wall': {
      const segs = hazardSegments(h, time);
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.lineWidth = BARRIER.thickness * u * 1.4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      for (const [a, b] of segs) {
        ctx.moveTo(cam.sx(a.x), cam.sy(a.y) + 2);
        ctx.lineTo(cam.sx(b.x), cam.sy(b.y) + 2);
      }
      ctx.stroke();
      return;
    }
  }
}

/**
 * One barrier / wall chord as an upright slab (y-sorted with the bodies): the effects library's face for it, else
 * the vector slab, front face below the top edge. `index` is the chord's place along the wall.
 */
export function drawHazardSegment(ctx: Ctx, cam: Camera, h: ViewHazard, seg: Segment, time: number, index = 0): void {
  if (drawWallFace(ctx, cam, h, seg, index, time)) return;
  const u = UNIT;
  const [a, b] = seg;
  const ax = cam.sx(a.x), ay = cam.sy(a.y), bx = cam.sx(b.x), by = cam.sy(b.y);
  const hpx = WALL_H * u;
  const thick = BARRIER.thickness * u;
  if (h.k === 'barrier') {
    const frac = clamp01(h.hp / BARRIER.hp);
    const alpha = 0.15 + 0.35 * frac + (frac < 0.25 ? 0.1 * Math.sin(time * 20) : 0);
    const pat = hexPattern(ctx);
    ctx.beginPath();
    ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.lineTo(bx, by - hpx); ctx.lineTo(ax, ay - hpx); ctx.closePath();
    ctx.fillStyle = rgba(ELEMENT_SWATCH.shield.core, alpha);
    ctx.fill();
    if (pat) { ctx.globalAlpha = alpha * 0.8; ctx.fillStyle = pat; ctx.fill(); ctx.globalAlpha = 1; }
    ctx.strokeStyle = rgba(ELEMENT_SWATCH.shield.core, 0.5 + 0.5 * frac);
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(ax, ay - hpx); ctx.lineTo(bx, by - hpx);
    ctx.stroke();
    return;
  }
  const solid = wallSolid(h);
  if (solid === 'earth') {
    const payload = h.el.find((e) => e !== 'shield' && e !== 'earth');
    ctx.fillStyle = ELEMENT_SWATCH.earth.dark;
    ctx.beginPath();
    ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.lineTo(bx, by - hpx); ctx.lineTo(ax, ay - hpx); ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = ELEMENT_SWATCH.earth.core;
    ctx.lineWidth = thick;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.moveTo(ax, ay - hpx); ctx.lineTo(bx, by - hpx);
    ctx.stroke();
    ctx.strokeStyle = rgba('#000000', 0.4);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo((ax + bx) / 2, ay - hpx * 0.5); ctx.lineTo((ax + bx) / 2 + 4, by - hpx * 0.1);
    ctx.stroke();
    if (payload) {
      ctx.globalCompositeOperation = 'lighter';
      drawGlow(ctx, elementColor(payload), (ax + bx) / 2, (ay + by) / 2 - hpx * 0.5, 40);
      ctx.globalCompositeOperation = 'source-over';
    }
  } else {
    ctx.fillStyle = rgba(ELEMENT_SWATCH.ice.core, 0.5);
    ctx.beginPath();
    ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.lineTo(bx, by - hpx * 1.1); ctx.lineTo(ax, ay - hpx * 1.1); ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(ax, ay - hpx * 1.1); ctx.lineTo(bx, by - hpx * 1.1);
    ctx.stroke();
    ctx.strokeStyle = rgba('#FFFFFF', 0.5);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(ax + (bx - ax) * 0.3, ay - hpx * 0.9); ctx.lineTo(ax + (bx - ax) * 0.5, by - hpx * 0.2);
    ctx.stroke();
  }
}

/** HP hint for a barrier / wall under the cursor (DESIGN 6.4 "hp bar on hover"). */
export function drawHazardHpHint(ctx: Ctx, cam: Camera, h: ViewHazard, cursor: Vec2, time: number): void {
  if (h.k !== 'barrier' && h.k !== 'wall') return;
  const segs = hazardSegments(h, time);
  let near = false;
  for (const [a, b] of segs) {
    const abx = b.x - a.x, aby = b.y - a.y;
    const l2 = abx * abx + aby * aby || 1;
    const t = clamp01(((cursor.x - a.x) * abx + (cursor.y - a.y) * aby) / l2);
    const qx = a.x + abx * t, qy = a.y + aby * t;
    if (Math.hypot(cursor.x - qx, cursor.y - qy) < 1.0) { near = true; break; }
  }
  if (!near) return;
  const max = h.k === 'barrier' ? BARRIER.hp : wallSolid(h) === 'ice' ? 1000 : 2500;
  const px = cam.sx(cursor.x), py = cam.sy(cursor.y) - 20;
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillRect(px - 26, py - 4, 52, 8);
  ctx.fillStyle = h.k === 'barrier' ? ELEMENT_SWATCH.shield.core : wallSolid(h) === 'ice' ? ELEMENT_SWATCH.ice.core : ELEMENT_SWATCH.earth.core;
  ctx.fillRect(px - 24, py - 2, 48 * clamp01(h.hp / max), 4);
}
